// @givmo/app-admin — Admin Team page (07-1.5b). Company admin RBAC.
//
// SaaS re-skin (R2): the same data wiring + behavior + testids as the parity rebuild, re-presented on
// the design-system SaaS components (PageHeader · DataTable · Badge · RowActions) — a RE-SKIN, not a
// rewrite. The api-client hooks, the Owner gate, the last-Owner safeguard, the self-removal sign-out,
// and every `data-testid` are unchanged.
//
// Wiring:
//   - LIST   → `companiesGetCompanyAdmins` (GET /admin/company/admins) via TanStack `useInfiniteQuery`,
//              page size 10, has-next driven by a short final page. Tolerant decode (`extractList`).
//              NO search box (Flutter parity).
//   - INVITE → POST via the shared <AdminEditInviteDialog> (invite mode).
//   - EDIT   → name PUT + conditional role PUT, via <AdminEditInviteDialog> (edit mode).
//   - REMOVE → DELETE `companiesRemoveAdminFromCompany`, always behind <AdminRemoveDialog> (the
//              destructive action is dialog-guarded). Self-removal signs out + navigates to login.
//
// OWNER GATE (07-1.4): role from GET /admin/company/me; Owner → invite/edit/remove controls visible,
// Member → read-only, a role-read ERROR FAILS OPEN. The root keeps `data-testid="page-admins"`.

import { useCallback, useMemo, useState } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryFunctionContext,
} from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  companiesGetCompanyAdminOptions,
  companiesGetCompanyOptions,
} from '@givmo/api-client/query';
import { sdk, isGivmoError, type AdminOut } from '@givmo/api-client';
import {
  Avatar,
  Badge,
  Button,
  DataTable,
  EmptyState,
  Icon,
  IconButton,
  PageHeader,
  SearchInput,
  testIdProp,
  toast,
  type DataTableColumn,
} from '@givmo/design-system';
import { UserPlus, Pencil, Trash2, Users } from 'lucide-react';
import { extractList } from '@givmo/api-client/list';
import { useAuth } from '../../auth/auth-context';
import { isCompanyJoinOrCreateEnabled } from '../../lib/env';
import { AUTH_ROUTES } from '../auth/auth-routing';
import { JoinRequestsSection } from '../join-requests/join-requests-section';
import { adminFullName } from './admins-decode';
import { ROLE_OWNER } from './admins-roles';
import { AdminEditInviteDialog } from './admin-edit-invite-dialog';
import { AdminRemoveDialog, type RemoveMode } from './admin-remove-dialog';

const PREFIX = 'admins';
const PAGE_LIMIT = 10;

const LIST_KEY = ['companies-get_company_admins', 'infinite'] as const;

async function fetchAdminsPage(offset: number, signal: AbortSignal): Promise<AdminOut[]> {
  const { data } = await sdk.companiesGetCompanyAdmins({
    query: { 'page[limit]': PAGE_LIMIT, 'page[offset]': offset },
    signal,
    throwOnError: true,
  });
  return extractList<AdminOut>(data);
}

type DialogState =
  | { kind: 'none' }
  | { kind: 'invite' }
  | { kind: 'edit'; admin: AdminOut }
  | { kind: 'remove'; admin: AdminOut; mode: RemoveMode };

export function AdminsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { signOut } = useAuth();

  const meQuery = useQuery(companiesGetCompanyAdminOptions());
  const me = meQuery.data ?? null;
  const canManage = !(meQuery.isSuccess && me?.admin_company_role !== ROLE_OWNER);

  const companyQuery = useQuery(companiesGetCompanyOptions());
  const companyDomain = companyQuery.data?.domain ?? '';

  const [dialog, setDialog] = useState<DialogState>({ kind: 'none' });
  // Client-side filter over the loaded admins (the admins endpoint has no server search). Gives the
  // Admin Team table the same toolbar grammar as Employees without changing the api-client wiring.
  const [search, setSearch] = useState('');

  const list = useInfiniteQuery({
    queryKey: LIST_KEY,
    queryFn: ({ pageParam, signal }: QueryFunctionContext<typeof LIST_KEY, number>) =>
      fetchAdminsPage(pageParam, signal),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < PAGE_LIMIT ? undefined : allPages.reduce((n, p) => n + p.length, 0),
  });

  const admins = useMemo(() => (list.data ? list.data.pages.flat() : []), [list.data]);

  const filteredAdmins = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length === 0) return admins;
    return admins.filter(
      (a) => adminFullName(a).toLowerCase().includes(q) || a.email.toLowerCase().includes(q),
    );
  }, [admins, search]);

  const canRemove = useCallback(
    (target: AdminOut): boolean => {
      if (target.admin_company_role !== ROLE_OWNER) return true;
      const ownerCount = admins.filter((a) => a.admin_company_role === ROLE_OWNER).length;
      return ownerCount > 1;
    },
    [admins],
  );

  // Stable so the column set that closes over it can be, which is what lets the rows skip re-render.
  const openRemove = useCallback(
    (admin: AdminOut) => {
      const mode: RemoveMode = !canRemove(admin)
        ? 'blocked'
        : me?.id !== undefined && admin.id === me.id
          ? 'self'
          : 'normal';
      setDialog({ kind: 'remove', admin, mode });
    },
    [canRemove, me?.id],
  );

  const remove = useMutation({
    mutationFn: (admin: AdminOut) =>
      sdk.companiesRemoveAdminFromCompany({
        path: { target_user_id: admin.id },
        throwOnError: true,
      }),
    onSuccess: async (_data, admin) => {
      const wasSelf = me?.id !== undefined && admin.id === me.id;
      setDialog({ kind: 'none' });
      if (wasSelf) {
        await signOut();
        // EP-SUITE2-FIX5 ⚠ CLEAR BEFORE THE DOOR (the shell's idiom, `app-shell.tsx`). React commits the
        // sign-out a render after `signOut()` resolves, so `/login-home`'s arrival guard still sees this
        // account — and with its company cached it answered from the cache and sent the signed-out
        // visitor through `/dashboard` to `/login-home?redirect=%2Fdashboard`. Cleared, the guard's read
        // goes out with no token and the identity boundary aborts it (`app.sign-out-door.test.tsx`).
        queryClient.clear();
        await navigate({ to: AUTH_ROUTES.loginHome });
        return;
      }
      toast.success('Admin removed');
      await queryClient.invalidateQueries({ queryKey: LIST_KEY });
    },
    onError: (error: unknown) => {
      toast.error(isGivmoError(error) ? error.message : 'Something went wrong. Please try again.');
    },
  });

  const removeTarget = dialog.kind === 'remove' ? dialog : null;

  // ── Columns (re-skin: name+avatar, role badge, owner-only kebab). ──────────────────────────────
  // Memoized so rows skip re-render on unrelated parent state. The deps are exactly what the
  // cells close over, so a real change still rebuilds them.
  const columns: DataTableColumn<AdminOut>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Name',
        width: '40%',
        cell: (admin) => (
          <div className="flex min-w-0 items-center gap-12">
            <Avatar name={adminFullName(admin)} src={admin.logo_url ?? undefined} size="sm" />
            <div className="min-w-0">
              <div className="truncate font-w600 text-text-strong">{adminFullName(admin)}</div>
              <div className="truncate text-s13 text-text-tertiary">{admin.email}</div>
            </div>
          </div>
        ),
      },
      {
        id: 'role',
        header: 'Role',
        cell: (admin) =>
          admin.admin_company_role === ROLE_OWNER ? (
            <Badge tone="brand">Owner</Badge>
          ) : admin.admin_company_role ? (
            <Badge tone="neutral">{admin.admin_company_role}</Badge>
          ) : null,
      },
      // Actions column is appended only when the current admin can manage (Owner / fail-open). Edit +
      // Remove are always-present row controls (the e2e/test contract queries their testids without
      // opening a menu); Remove is dialog-guarded (AdminRemoveDialog) and uses the danger affordance.
      ...(canManage
        ? [
            {
              id: 'actions',
              header: '',
              align: 'right' as const,
              width: '104px',
              cell: (admin: AdminOut) => (
                // Revealed on row-hover (+ keyboard focus, for a11y) — no resting wall of icons — and
                // always shown on a touch screen, which has no hover (EP-WEB42, `pointer-coarse`).
                <div className="flex items-center justify-end gap-2 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 pointer-coarse:opacity-100">
                  <IconButton
                    aria-label={`Edit ${adminFullName(admin)}`}
                    tone="ghost"
                    size="sm"
                    onClick={() => setDialog({ kind: 'edit', admin })}
                    testId={`${PREFIX}-row-${admin.id}-edit`}
                  >
                    <Icon icon={Pencil} size={16} aria-hidden />
                  </IconButton>
                  <IconButton
                    aria-label={`Remove ${adminFullName(admin)}`}
                    tone="danger"
                    size="sm"
                    onClick={() => openRemove(admin)}
                    testId={`${PREFIX}-row-${admin.id}-remove`}
                  >
                    <Icon icon={Trash2} size={16} aria-hidden />
                  </IconButton>
                </div>
              ),
            },
          ]
        : []),
    ],
    [canManage, openRemove],
  );

  return (
    <div
      className="mx-auto flex w-full max-w-[1100px] flex-col gap-24 px-24 py-24"
      {...testIdProp('page-admins')}
    >
      <PageHeader
        title="Admin Team"
        subtitle="Manage who can administer your company's giving program."
        actions={
          canManage ? (
            <Button
              appearance="saas"
              tone="primary"
              leadingIcon={<Icon icon={UserPlus} size={16} aria-hidden />}
              onClick={() => setDialog({ kind: 'invite' })}
              testId={`${PREFIX}-invite`}
            >
              Invite admin
            </Button>
          ) : undefined
        }
      />

      <DataTable
        testId={`${PREFIX}-list`}
        columns={columns}
        rows={filteredAdmins}
        rowKey={(a) => a.id}
        rowTestId={() => `${PREFIX}-row`}
        loading={list.isPending}
        error={
          list.isError
            ? isGivmoError(list.error)
              ? list.error.message
              : 'Failed to load admins.'
            : undefined
        }
        onRetry={() => void list.refetch()}
        toolbar={
          // Same toolbar grammar as Employees: a left-aligned SearchInput (client filter).
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClear={() => setSearch('')}
            placeholder="Search admins…"
            aria-label="Search admins"
            testId={`${PREFIX}-search`}
          />
        }
        emptyState={
          <EmptyState
            testId={`${PREFIX}-empty-state`}
            icon={<Icon icon={Users} size={20} aria-hidden />}
            title="No admins found"
            description={
              search.trim().length > 0
                ? 'No admins match your search. Try a different name or email.'
                : 'Invite a teammate to help administer your giving program.'
            }
            action={
              canManage && search.trim().length === 0 ? (
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  leadingIcon={<Icon icon={UserPlus} size={14} aria-hidden />}
                  onClick={() => setDialog({ kind: 'invite' })}
                >
                  Invite admin
                </Button>
              ) : undefined
            }
          />
        }
        footer={
          <>
            <span className="font-sans text-s13 tracking-normal text-text-tertiary tabular-nums">
              {filteredAdmins.length} {filteredAdmins.length === 1 ? 'admin' : 'admins'}
            </span>
            {list.hasNextPage ? (
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                disabled={list.isFetchingNextPage}
                onClick={() => void list.fetchNextPage()}
                testId={`${PREFIX}-load-more`}
              >
                {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            ) : null}
          </>
        }
      />

      {/* EP-SUITE2 — Owner "Join requests" inbox. Dark behind the join-or-create flag; Owner-only
          (rendered only when `canManage`, and the backend list route 403s a Member). */}
      {isCompanyJoinOrCreateEnabled() && canManage && <JoinRequestsSection rosterKey={LIST_KEY} />}

      {/* Invite / Edit dialog (one component, mode by `adminToEdit`). */}
      <AdminEditInviteDialog
        open={dialog.kind === 'invite' || dialog.kind === 'edit'}
        onOpenChange={(open) => {
          if (!open) setDialog({ kind: 'none' });
        }}
        adminToEdit={dialog.kind === 'edit' ? dialog.admin : null}
        companyDomain={companyDomain}
        invalidateKey={LIST_KEY}
      />

      {/* Remove dialog (three modes). */}
      <AdminRemoveDialog
        open={removeTarget !== null}
        onOpenChange={(open) => {
          if (!open && !remove.isPending) setDialog({ kind: 'none' });
        }}
        mode={removeTarget?.mode ?? 'normal'}
        name={removeTarget ? adminFullName(removeTarget.admin) : ''}
        loading={remove.isPending}
        onConfirm={() => {
          if (removeTarget) remove.mutate(removeTarget.admin);
        }}
      />
    </div>
  );
}
